import { BookingLayout, GlobalLayout, MainLayout } from 'app/layouts';
import { getFeatureFlags } from 'app/appState';
import { store } from 'app/config/storeConfig/storeConfig';
import { getInfoFromToken, isAuthKeyEmpty, UserRoles } from 'entities/User';
import { AdminPanelPage } from 'pages/AdminPanelPage';
import { ArrivalsPage } from 'pages/ArrivalsPage';
import { PaymentsStatisticsPage } from 'pages/PaymentsStatisticsPage';
import { BookingPage } from 'pages/BookingPage';
import { BoxesOccupancyPage } from 'pages/BoxesOccupancyPage';
import { BoxesPage } from 'pages/BoxesPage';
import { CheckoutPage } from 'pages/CheckoutPage';
import { CitiesPage } from 'pages/CitiesPage';
import { ContractsPage } from 'pages/ContractsPage';
import { CreditNotesPage } from 'pages/CreditNotesPage';
import { DepositsPage } from 'pages/DepositsPage';
import { DiscountsPage } from 'pages/DiscountsPage';
import { ForgotPasswordPage } from 'pages/ForgotPasswordPage';
import { HistoryPage } from 'pages/HistoryPage';
import { IncomeStatisticsPage } from 'pages/IncomeStatisticsPage';
import { InsuranceSettingsPage } from 'pages/InsuranceSettingsPage';
import { InvoicesInsurancePage } from 'pages/InvoicesInsurancePage';
import { SelfIssuedReceiptsPage } from 'pages/SelfIssuedReceiptsPage';
import { InvoicesPage } from 'pages/InvoicesPage';
import { LateChargeSettingsPage } from 'pages/LateChargeSettingsPage';
import { LoginPage } from 'pages/LoginPage';
import { MyBoxesPage } from 'pages/MyBoxesPage';
import { NoResultPage } from 'pages/NoResultPage';
import { NotesPage } from 'pages/NotesPage';
import { NotFoundPage } from 'pages/NotFoundPage';
import { PaymentFailedPage } from 'pages/PaymentFailedPage';
import { PaymentReturnPage } from 'pages/PaymentReturnPage';
import { PaymentSuccessPage } from 'pages/PaymentSuccessPage';
import { ProfilePage } from 'pages/ProfilePage';
import { ReservationsPage } from 'pages/ReservationsPage';
import { ResetPasswordPage } from 'pages/ResetPasswordPage';
import { UserContractsPage } from 'pages/UserContractsPage';
import { UsersPage } from 'pages/UsersPage';
import { WarehouseConstructorPage } from 'pages/WarehouseConstructorPage';
import { WarehousePage } from 'pages/WarehousePage';
import { WarehousesPage } from 'pages/WarehousesPage';
import { createBrowserRouter, LoaderFunction, LoaderFunctionArgs, redirect, redirectDocument, RouteObject } from 'react-router-dom';
import { REDIRECTED_FROM_URL_KEY } from './constants';
import { AppRoutes } from './types';
import { BoxesRevenuePage } from 'pages/BoxesRevenuePage';
import { WaitingListPage } from 'pages/WaitingListPage';

const protectedRouteLoader =
  (allowedRoles: UserRoles[]) =>
  ({ request }: LoaderFunctionArgs): Nullable<Response> => {
    const isAuthorized = !isAuthKeyEmpty();
    const userRole = getInfoFromToken()?.role;

    const hasAccess = userRole ? allowedRoles.includes(userRole) : false;

    if (!isAuthorized || !userRole || !hasAccess) {
      sessionStorage.setItem(REDIRECTED_FROM_URL_KEY, request.url);

      return redirect(AppRoutes.LOGIN);
    }
    sessionStorage.removeItem(REDIRECTED_FROM_URL_KEY);

    return null;
  };

const rootRouteLoader = (): Response => {
  const userRole = getInfoFromToken()?.role;

  const redirectedFromURL = sessionStorage.getItem(REDIRECTED_FROM_URL_KEY);

  const redirectionMap: Record<UserRoles, AppRoutes> = {
    [UserRoles.SUPERUSER]: AppRoutes.WAREHOUSES,
    [UserRoles.MANAGER]: AppRoutes.WAREHOUSES,
    [UserRoles.CUSTOMER]: AppRoutes.MY_BOXES,
    [UserRoles.STAFF]: AppRoutes.BOXES,
    [UserRoles.PARTNER]: AppRoutes.WAREHOUSES,
  };

  return redirectedFromURL ? redirectDocument(redirectedFromURL) : redirect(userRole ? redirectionMap[userRole] : AppRoutes.BOOKING);
};

const checkAssignedWarehousesLoader = (): Nullable<Response> => {
  const isSuperuserRole = getInfoFromToken()?.role === UserRoles.SUPERUSER;

  const hasAssignedWarehouses = Boolean(getInfoFromToken()?.assignedWarehouseIds?.length);

  const showPage = isSuperuserRole || hasAssignedWarehouses;

  return showPage ? null : redirect(AppRoutes.NO_RESULT);
};

const loginPageLoader = (): Nullable<Response> => {
  const isAuthorized = !isAuthKeyEmpty();

  if (isAuthorized) {
    return redirect(AppRoutes.HOME);
  }

  return null;
};

const warehouseMapConstructorFeatureFlagLoader = ({ params }: LoaderFunctionArgs): Nullable<Response> => {
  const mapConstructorFlag = getFeatureFlags(store.getState())?.mapConstructor;
  const isMapConstructorDisabled = mapConstructorFlag === false;

  if (isMapConstructorDisabled) {
    return redirect(`${AppRoutes.WAREHOUSES}/${params.warehouseId}`);
  }

  return null;
};

const warehouseMapConstructorRouteLoader = async (args: LoaderFunctionArgs): Promise<Nullable<Response>> => {
  const authLoaderResult = await protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER])(args);

  if (authLoaderResult) {
    return authLoaderResult;
  }

  return warehouseMapConstructorFeatureFlagLoader(args);
};

const combineLoaders =
  (...loaders: LoaderFunction[]) =>
  async (args: CustomAny) => {
    let result = null;

    for (const loader of loaders) {
      result = await loader(args);
    }

    return result;
  };

const bookingRoutes: RouteObject[] = [
  {
    index: true,
    Component: BookingPage,
  },
];

const mainRoutes: RouteObject[] = [
  {
    path: AppRoutes.HOME,
    Component: MainLayout,
    children: [
      {
        index: true,
        loader: rootRouteLoader,
      },
      {
        path: AppRoutes.WAREHOUSES,
        Component: WarehousesPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.STAFF, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: `${AppRoutes.WAREHOUSES}/:warehouseId`,
        Component: WarehousePage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: `${AppRoutes.WAREHOUSES}/:warehouseId/constructor`,
        Component: WarehouseConstructorPage,
        loader: warehouseMapConstructorRouteLoader,
      },
      {
        path: AppRoutes.CITIES,
        Component: CitiesPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: AppRoutes.USERS,
        Component: UsersPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: `${AppRoutes.USERS}/:userId/contracts`,
        Component: UserContractsPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.CONTRACTS,
        Component: ContractsPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.RESERVATIONS,
        Component: ReservationsPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.STATISTICS,
        Component: null,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
      },
      {
        path: AppRoutes.BOXES_OCCUPANCY,
        Component: BoxesOccupancyPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
      },
      {
        path: AppRoutes.INCOME_STATISTICS,
        Component: IncomeStatisticsPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
      },
      {
        path: AppRoutes.ARRIVALS_STATISTICS,
        Component: ArrivalsPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
      },
      {
        path: AppRoutes.PAYMENTS_STATISTICS,
        Component: PaymentsStatisticsPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
      },
      {
        path: AppRoutes.BOXES_REVENUE,
        Component: BoxesRevenuePage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
      },
      {
        path: AppRoutes.INVOICES,
        Component: InvoicesPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.CREDIT_NOTES,
        Component: CreditNotesPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.INSURANCE,
        Component: InvoicesInsurancePage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.DEPOSITS,
        Component: DepositsPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.SELF_ISSUED_RECEIPTS,
        Component: SelfIssuedReceiptsPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.BOXES,
        Component: BoxesPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.STAFF, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.HISTORY,
        Component: HistoryPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.STAFF, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.NOTES,
        Component: NotesPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: AppRoutes.TASKS,
        Component: null,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: AppRoutes.GENERAL,
        Component: null,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: AppRoutes.BILLING_DOCUMENTS,
        Component: null,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.INSURANCE_SETTINGS,
        Component: InsuranceSettingsPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: AppRoutes.LATE_CHARGE_SETTINGS,
        Component: LateChargeSettingsPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: AppRoutes.MY_BOXES,
        Component: MyBoxesPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.CUSTOMER, UserRoles.STAFF, UserRoles.PARTNER]),
      },
      {
        path: AppRoutes.GLOBAL_SETTINGS,
        Component: null,
        loader: protectedRouteLoader([UserRoles.SUPERUSER]),
      },
      {
        path: AppRoutes.ADMIN_PANEL,
        Component: AdminPanelPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
      {
        path: `${AppRoutes.PROFILE}/:userId`,
        Component: ProfilePage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.CUSTOMER, UserRoles.STAFF, UserRoles.PARTNER]),
      },
      {
        path: AppRoutes.NO_RESULT,
        Component: NoResultPage,
      },
      {
        path: AppRoutes.DISCOUNTS,
        Component: DiscountsPage,
        loader: combineLoaders(
          protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.PARTNER]),
          checkAssignedWarehousesLoader,
        ),
      },
      {
        path: AppRoutes.WAITING_LIST,
        Component: WaitingListPage,
        loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER]),
      },
    ],
  },
  {
    path: AppRoutes.BOOKING,
    Component: BookingLayout,
    children: bookingRoutes,
  },
  {
    path: AppRoutes.LOGIN,
    Component: LoginPage,
    loader: loginPageLoader,
  },
  {
    path: AppRoutes.FORGOT_PASSWORD,
    Component: ForgotPasswordPage,
    loader: loginPageLoader,
  },
  {
    path: `${AppRoutes.RESET_PASSWORD}/:resetPasswordId`,
    Component: ResetPasswordPage,
    loader: loginPageLoader,
  },
  {
    path: AppRoutes.NOT_FOUND,
    Component: NotFoundPage,
  },
  {
    path: AppRoutes.CHECKOUT,
    Component: CheckoutPage,
    loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.CUSTOMER, UserRoles.PARTNER, UserRoles.STAFF]),
  },
  {
    path: AppRoutes.PAYMENT_SUCCESS,
    Component: PaymentSuccessPage,
    loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.CUSTOMER, UserRoles.PARTNER, UserRoles.STAFF]),
  },
  {
    path: AppRoutes.PAYMENT_RETURN,
    Component: PaymentReturnPage,
  },
  {
    path: AppRoutes.PAYMENT_FAILED,
    Component: PaymentFailedPage,
    loader: protectedRouteLoader([UserRoles.SUPERUSER, UserRoles.MANAGER, UserRoles.CUSTOMER, UserRoles.STAFF, UserRoles.PARTNER]),
  },
];

export const appRouter = createBrowserRouter([
  {
    Component: GlobalLayout,
    children: mainRoutes,
  },
]);
